import { resolveApiErrorMessage } from "./error";

export type ProfileSkillWriteErrorKind =
  | { kind: "validation"; message: string }
  | { kind: "rate_limited" }
  | { kind: "creation_quota"; retryAfterSeconds?: number }
  | { kind: "claim_conflict" }
  | { kind: "version_conflict" }
  | { kind: "generic"; message: string };

type ErrorResponse = {
  status?: number;
  data?: { code?: unknown; retryAfterSeconds?: unknown };
};

/**
 * Il testo che il BE ha scritto per l'utente, e solo quello. Si passa la sola
 * `response` e non l'errore: senza la risposta `resolveApiErrorMessage` ripiega
 * su `error.message`, che per axios è inglese tecnico («Network Error»,
 * «Request failed with status code 500») e non deve arrivare a schermo.
 */
const responseMessage = (response: ErrorResponse): string =>
  resolveApiErrorMessage({ response }, "");

export const mapProfileSkillWriteError = (
  error: unknown,
): ProfileSkillWriteErrorKind => {
  const response =
    typeof error === "object" && error !== null && "response" in error
      ? (error as { response?: ErrorResponse }).response
      : undefined;
  const code = response?.data?.code;

  if (response?.status === 400) {
    return { kind: "validation", message: responseMessage(response) };
  }
  if (response?.status === 429 && code === "SKILL_CREATION_QUOTA_EXCEEDED") {
    const retryAfter = response.data?.retryAfterSeconds;
    return {
      kind: "creation_quota",
      retryAfterSeconds:
        typeof retryAfter === "number" && Number.isFinite(retryAfter)
          ? Math.max(0, Math.ceil(retryAfter))
          : undefined,
    };
  }
  if (response?.status === 429) return { kind: "rate_limited" };
  if (response?.status === 409 && code === "PROFILE_VERSION_CONFLICT") {
    return { kind: "version_conflict" };
  }
  if (response?.status === 503 && code === "SKILL_EMBEDDING_CLAIM_CONFLICT") {
    return { kind: "claim_conflict" };
  }

  /* Rete giù o 5xx: non c'è niente da dire oltre al «riprova», e il testo di
     un 5xx («Internal server error») è scritto per i log, non per l'utente. */
  if (!response?.status || response.status >= 500) {
    return { kind: "generic", message: "" };
  }
  return { kind: "generic", message: responseMessage(response) };
};

/**
 * Quanto aspettare, detto come lo direbbe una persona: il BE manda i secondi
 * (con la quota di creazione sempre `86400`), e «86400 secondi» non è
 * un'attesa che si legge. Da 20 ore in su è «domani».
 *
 * Si arrotonda per eccesso: meglio riprovare un po' dopo che trovare la quota
 * ancora chiusa.
 */
export type RetryWait =
  | { unit: "tomorrow" }
  | { unit: "hours"; count: number }
  | { unit: "minutes"; count: number };

const TOMORROW_FROM_SECONDS = 20 * 60 * 60;

export const describeRetryWait = (seconds: number): RetryWait => {
  if (seconds >= TOMORROW_FROM_SECONDS) return { unit: "tomorrow" };
  const minutes = Math.max(1, Math.ceil(seconds / 60));
  if (minutes < 60) return { unit: "minutes", count: minutes };
  return { unit: "hours", count: Math.ceil(seconds / 3600) };
};
